import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Minus, Plus, Trash2, Info, CalendarDays, Clock } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useTimeSlotLimits, type TimeSlotLimit } from '@/hooks/useTimeSlotLimits';
import { generateAvailableTimes, type TimeSlotRange } from '@/lib/timeBookingUtils';

interface TimeSlotForLimits {
  id: string;
  start_time: string;
  end_time: string;
  available_days?: number[];
  duration_override_minutes?: number | null;
}

interface TimeSlotLimitsSettingsProps {
  establishmentId: string;
  timeSlots: TimeSlotForLimits[];
  disabled?: boolean;
  bookingIntervalMinutes?: 15 | 30 | 60;
  reservationDurationMinutes?: number;
}

const DAY_NAMES = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

type TargetMode = 'all' | 'slot' | 'specific_time';

function formatSlotTime(start: string, end: string): string {
  return `${start.substring(0, 5)} - ${end.substring(0, 5)}`;
}

function formatTime(t: string): string {
  return t.substring(0, 5);
}

export function TimeSlotLimitsSettings({
  establishmentId,
  timeSlots,
  disabled = false,
  bookingIntervalMinutes = 30,
  reservationDurationMinutes = 120,
}: TimeSlotLimitsSettingsProps) {
  const { limits, upsertLimit, deleteLimit } = useTimeSlotLimits({ establishmentId });
  const [addingDay, setAddingDay] = useState<number | null>(null);
  const [targetMode, setTargetMode] = useState<TargetMode>('all');
  const [addingSlotId, setAddingSlotId] = useState<string>('all');
  const [addingTimes, setAddingTimes] = useState<string[]>([]);
  const [addingType, setAddingType] = useState<'reservations' | 'people'>('reservations');
  const [addingValue, setAddingValue] = useState(5);
  const [isSaving, setIsSaving] = useState(false);

  // Heures bookables disponibles pour le jour choisi
  const availableTimesForDay = useMemo(() => {
    if (addingDay === null) return [];
    const slotsForDay: TimeSlotRange[] = timeSlots
      .filter(s => !s.available_days || s.available_days.length === 0 || s.available_days.includes(addingDay))
      .map(s => ({
        start: s.start_time,
        end: s.end_time,
        durationOverrideMinutes: s.duration_override_minutes ?? null,
      }));
    return generateAvailableTimes(slotsForDay, bookingIntervalMinutes, reservationDurationMinutes);
  }, [addingDay, timeSlots, bookingIntervalMinutes, reservationDurationMinutes]);

  const resetForm = () => {
    setAddingDay(null);
    setTargetMode('all');
    setAddingSlotId('all');
    setAddingTimes([]);
  };

  const handleAdd = async () => {
    if (addingDay === null) return;
    setIsSaving(true);
    try {
      if (targetMode === 'specific_time') {
        if (addingTimes.length === 0) {
          toast({ title: 'Sélectionne au moins une heure', variant: 'destructive' });
          setIsSaving(false);
          return;
        }
        for (const time of addingTimes) {
          await upsertLimit(addingDay, null, addingType, addingValue, time);
        }
        toast({
          title: 'Limites ajoutées',
          description: `${DAY_NAMES[addingDay]} — ${addingTimes.length} heure(s) à max ${addingValue} ${addingType === 'reservations' ? 'réservations' : 'personnes'}`,
        });
      } else {
        await upsertLimit(
          addingDay,
          targetMode === 'slot' && addingSlotId !== 'all' ? addingSlotId : null,
          addingType,
          addingValue,
          null
        );
        toast({ title: 'Limite ajoutée', description: `${DAY_NAMES[addingDay]} — max ${addingValue} ${addingType === 'reservations' ? 'réservations' : 'personnes'}` });
      }
      resetForm();
    } catch (e) {
      console.error(e);
      toast({ title: 'Erreur', description: "Impossible d'ajouter la limite.", variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (limit: TimeSlotLimit) => {
    try {
      await deleteLimit(limit.id);
      toast({ title: 'Limite supprimée' });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de supprimer la limite.', variant: 'destructive' });
    }
  };

  const handleUpdateValue = async (limit: TimeSlotLimit, newValue: number) => {
    if (newValue < 1) return;
    try {
      await upsertLimit(limit.day_of_week, limit.time_slot_id, limit.limit_type, newValue, limit.specific_time);
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const limitsByDay = DAY_NAMES.map((_, dayIndex) =>
    limits.filter(l => l.day_of_week === dayIndex)
  );

  const getTargetLabel = (limit: TimeSlotLimit) => {
    if (limit.specific_time) return formatTime(limit.specific_time);
    if (!limit.time_slot_id) return 'Tous les créneaux';
    const slot = timeSlots.find(s => s.id === limit.time_slot_id);
    return slot ? formatSlotTime(slot.start_time, slot.end_time) : 'Créneau inconnu';
  };

  const toggleTime = (time: string) => {
    setAddingTimes(prev => prev.includes(time) ? prev.filter(t => t !== time) : [...prev, time].sort());
  };

  const availableDays = DAY_NAMES.map((_, i) => i);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-muted-foreground" />
        <Label className="text-sm font-medium">Limites conditionnelles (par jour / créneau / heure)</Label>
      </div>

      {limits.length > 0 && (
        <div className="space-y-2">
          {DAY_NAMES.map((dayName, dayIndex) => {
            const dayLimits = limitsByDay[dayIndex];
            if (dayLimits.length === 0) return null;
            return (
              <div key={dayIndex} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{dayName}</p>
                {dayLimits.map(limit => (
                  <Card key={limit.id} className="flex items-center justify-between p-2 px-3">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <Badge variant={limit.specific_time ? 'default' : 'outline'} className="text-xs gap-1">
                        {limit.specific_time && <Clock className="w-3 h-3" />}
                        {getTargetLabel(limit)}
                      </Badge>
                      <span className="text-muted-foreground">—</span>
                      <span>max</span>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={disabled || limit.limit_value <= 1}
                          onClick={() => handleUpdateValue(limit, limit.limit_value - 1)}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="font-semibold w-6 text-center">{limit.limit_value}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={disabled}
                          onClick={() => handleUpdateValue(limit, limit.limit_value + 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                      <span className="text-muted-foreground text-xs">
                        {limit.limit_type === 'reservations' ? 'réservations' : 'personnes'}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      disabled={disabled}
                      onClick={() => handleDelete(limit)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </Card>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {addingDay !== null ? (
        <Card className="p-3 space-y-3 border-dashed">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Jour</Label>
              <Select value={addingDay.toString()} onValueChange={(v) => setAddingDay(parseInt(v))} disabled={disabled}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableDays.map(d => (
                    <SelectItem key={d} value={d.toString()}>{DAY_NAMES[d]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cible</Label>
              <Select
                value={targetMode}
                onValueChange={(v) => { setTargetMode(v as TargetMode); setAddingTimes([]); }}
                disabled={disabled}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout le jour</SelectItem>
                  <SelectItem value="slot">Un créneau entier</SelectItem>
                  <SelectItem value="specific_time">Heure(s) spécifique(s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {targetMode === 'slot' && (
            <div className="space-y-1">
              <Label className="text-xs">Créneau</Label>
              <Select value={addingSlotId} onValueChange={setAddingSlotId} disabled={disabled}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(slot => (
                    <SelectItem key={slot.id} value={slot.id}>
                      {formatSlotTime(slot.start_time, slot.end_time)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {targetMode === 'specific_time' && (
            <div className="space-y-1">
              <Label className="text-xs">Heures à limiter (sélectionne-en une ou plusieurs)</Label>
              {availableTimesForDay.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">
                  Aucune heure disponible pour ce jour. Vérifie tes créneaux et leur disponibilité.
                </p>
              ) : (
                <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto p-1 border rounded-md">
                  {availableTimesForDay.map(time => {
                    const checked = addingTimes.includes(time);
                    return (
                      <label
                        key={time}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs hover:bg-muted ${checked ? 'bg-primary/10' : ''}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleTime(time)}
                          disabled={disabled}
                          className="h-3.5 w-3.5"
                        />
                        <span className="font-mono">{time}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {addingTimes.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {addingTimes.length} heure(s) sélectionnée(s) — une ligne sera créée par heure.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Type de limite</Label>
              <Select value={addingType} onValueChange={(v) => setAddingType(v as 'reservations' | 'people')} disabled={disabled}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reservations">Réservations</SelectItem>
                  <SelectItem value="people">Personnes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Valeur max</Label>
              <div className="flex items-center gap-1">
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={disabled || addingValue <= 1} onClick={() => setAddingValue(Math.max(1, addingValue - 1))}>
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="text-lg font-semibold w-10 text-center">{addingValue}</span>
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={disabled} onClick={() => setAddingValue(addingValue + 1)}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={handleAdd} disabled={isSaving || disabled} className="flex-1">
              {isSaving ? 'Ajout...' : 'Ajouter'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={resetForm} disabled={isSaving}>
              Annuler
            </Button>
          </div>
        </Card>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAddingDay(1)}
          disabled={disabled}
          className="w-full"
        >
          + Ajouter une limite conditionnelle
        </Button>
      )}

      <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="text-blue-800 dark:text-blue-300 text-xs space-y-1.5">
          <p className="font-medium">Comment fonctionnent les limites ?</p>
          <p>
            Chaque limite s'applique <strong>par horaire de réservation individuel</strong>.
            Tu peux cibler tout le jour, un créneau entier, ou une (ou plusieurs) heure(s) précise(s) comme 19:00 ou 19:30.
          </p>
          <p>
            <strong>Hiérarchie</strong> (du plus prioritaire au moins prioritaire) :
            Heure précise → Jour + créneau → Jour seul → Limite globale.
            Seule la règle la plus spécifique s'applique, elles ne se cumulent pas.
          </p>
          <p>
            <strong>Exemple :</strong> Limite "Vendredi 19:00 → max 4 personnes". À 19:00 le vendredi,
            on n'acceptera plus que 4 personnes au total. À 19:15 et 19:30, la limite parente reprend.
          </p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
